import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import type { ShoppingCartItem } from '@/models/ShoppingCartItem';
import { ExtendedVenueType } from '@/models/ExtendedVenueType';
import { createVenueURL, getOfferingImage } from '@/utils/venueDetailsHelper';
import type { BasicProfile } from '@/models/CsnFullVenueProfile';
import UrlBuilder from '@/utils/urlBuilder';
import { CSN_FACT_SOURCE_MAP } from '@/utils/analyticsClientHelper';
import { Locale } from '@/i18n';

export function getShoppingCartItem(
  venue: CsnSearchVenue,
  profileUrl: string,
  searchResultsRank: number,
  searchTerm: string,
  venueIndex: number,
  promotionId?: string,
  promotionTitle?: string,
  promotionType?: string,
  source?: string
): ShoppingCartItem {
  let adId;
  const isBrandPage = venue.featuredLevel && venue.listingOwnerType === 'BRAND_PAGE';
  const hideVenueStats = Boolean(isBrandPage || (venue.featuredLevel === 'DIAMOND_PLUS' && venue.adText));
  let adType = venue.featuredLevel;
  if (venue.featuredLevel === 'DIAMOND_PLUS') {
    adType = hideVenueStats ? 'DIAMOND_PLUS_TEXT' : 'DIAMOND_PLUS_STATS';
  }
  const adPosition = venue.listingCode ? venueIndex + 1 : 'null';

  if (!promotionId) {
    adId = venue.listingCode || 'null';
  }
  return {
    venueId: venue.id,
    adId,
    adType,
    adPosition,
    promotionId,
    promotionTitle,
    promotionType,
    searchTerm,
    type:
      venue.type === ExtendedVenueType.Values.CVB && venue.brandName === ExtendedVenueType.Values.DMC
        ? venue.brandName
        : venue.type,
    ofrgImage: getOfferingImage(venue.mainImage),
    city: venue.city,
    name: venue.name,
    stateProvinceCode: venue.stateProvinceCode,
    countryCode: venue.countryCode,
    url: profileUrl,
    searchResultsRank,
    diamondLevel: venue.diamondLevel,
    featuredLevel: venue.featuredLevel,
    listingId: venue.listingId,
    hasPromotions: venue.promotionsAllowed ?? false,
    latitude: venue?.geographicLocation?.lat,
    longitude: venue?.geographicLocation?.lon,
    isSTEnabled: venue.tags?.includes('SOCIAL_TABLES_ENABLED') ?? false,
    source: source ?? 'venue_card',
    fastResponder: venue.fastResponder
  };
}

export function getShoppingCartItemForProfile(
  venue: BasicProfile,
  profileUrl: string,
  searchTerm: string,
  promotionId?: string,
  promotionTitle?: string,
  promotionType?: string,
  source?: string
): ShoppingCartItem {
  return {
    venueId: venue.id,
    promotionId,
    promotionTitle,
    promotionType,
    searchTerm,
    type: venue.venueTypeId,
    ofrgImage: getOfferingImage(venue.ofrgImage),
    city: venue.city,
    name: venue.name,
    stateProvinceCode: venue.stateProvinceCode && venue.stateProvinceCode !== 'BLANK' ? venue.stateProvinceCode : '',
    countryCode: venue.countryCode,
    url: profileUrl,
    diamondLevel: venue.diamondLevel,
    hasPromotions: venue.hasPromotions ?? false,
    latitude: venue.latitude,
    longitude: venue.longitude,
    isSTEnabled: venue.socialTablesEnabled ?? false,
    source: source ?? 'venue_profile',
    fastResponder: venue.fastResponder
  };
}

export function getMappedShoppingCartItems(
  venues: CsnSearchVenue[],
  locale: Locale,
  searchTerm: string,
  cventGuestDomain: string,
  basePath: string
): Map<string, ShoppingCartItem> {
  const venueItemsForShoppingCart = new Map(
    venues.map((venue, index) => {
      const profileUrlForCart = new UrlBuilder()
        .mergeLocationObject(
          createVenueURL(venue.id, venue.name, venue.type, venue.city, locale, cventGuestDomain, basePath)
        )
        .getFullUrl();
      const shoppingCartItem = getShoppingCartItem(
        venue,
        profileUrlForCart,
        index + 1,
        searchTerm,
        index,
        undefined,
        undefined,
        undefined,
        CSN_FACT_SOURCE_MAP
      );
      return [venue.id, shoppingCartItem];
    })
  );
  return venueItemsForShoppingCart;
}
